/**
 * router.ts — a tiny hash router for the Vendor Portal.
 * =====================================================================
 *
 * Mirrors the prototype's showView()/nav() model (mockups/mango_portal_complete
 * .html:1888-1928) but framework-free. Each "view" maps to a registered handler
 * that mounts the right Web Component into the shell's <main> outlet.
 *
 * Hash-based on purpose: the SPA is served from S3 behind CloudFront with a
 * single index.html. Hash routing needs no server rewrite per path, so deep
 * links and refreshes never 404 (the existing SPA-rewrite CloudFront Function
 * covers history routing too, but hash keeps Step-1 dependency-free).
 *
 * Navigation is also CSP-safe: links use `<a href="#/jobs">`, and the router
 * listens to `hashchange` — no inline JS.
 */

export type RouteHandler = (params: RouteParams) => void;
export interface RouteParams {
  /** Path segments after the route name, e.g. #/jobs/MNG-1044 → ["MNG-1044"]. */
  segments: string[];
}

export class Router {
  private routes = new Map<string, RouteHandler>();
  private fallback: RouteHandler | null = null;

  /** Register a view handler under a route name (e.g. "jobs"). */
  on(name: string, handler: RouteHandler): this {
    this.routes.set(name, handler);
    return this;
  }

  /** Handler used when no route matches (or the hash is empty). */
  setFallback(handler: RouteHandler): this {
    this.fallback = handler;
    return this;
  }

  /** Programmatic navigation (sets the hash; triggers the handler). */
  navigate(name: string, ...segments: string[]): void {
    const path = ["#", name, ...segments].join("/").replace("#/", "#/");
    window.location.hash = segments.length ? `#/${name}/${segments.join("/")}` : `#/${name}`;
    if (window.location.hash === path) this.resolve();
  }

  /** Start listening and resolve the current hash immediately. */
  start(): void {
    window.addEventListener("hashchange", () => this.resolve());
    this.resolve();
  }

  private resolve(): void {
    const raw = window.location.hash.replace(/^#\/?/, "");
    const parts = raw.split("/").filter(Boolean);
    const name = parts[0] ?? "";
    const handler = this.routes.get(name);
    const params: RouteParams = { segments: parts.slice(1) };
    if (handler) {
      handler(params);
    } else if (this.fallback) {
      this.fallback(params);
    }
  }
}
